import { useState } from "react";

import { ClickableTextPanel } from "@/Common/Components/ClickableText/ClickableTextPanel";
import { ComboBoxDropDownItem } from "@/Common/Components/Inputs/Combobox";
import { ComboBoxControl } from "@/Common/Components/Inputs/ComboboxControl";
import { TextAreaAutoSize } from "@/Common/Components/Inputs/TextAreaAutoSize";
import { Popup } from "@/Common/Components/Modals/Popup";
import { useTranslation } from "@/Common/CustomHooks/useTranslation";
import { PrescriptionRenewalMedications } from "../Models/PrescriptionRenewalMedications";

import styles from "./PrescriptionRenewalsViewDenyPopup.module.scss";

interface IPrescriptionRenewalsViewDenyPopupProps {
    relatedMedication?: PrescriptionRenewalMedications;
    isReadonly?: boolean;
    onSave: (rejectReason: string, rejectNote: string) => void;
    onCancel: () => void;
}

const notesTextareaId: string = "notesTextsAreaField";
const notesTextareaFixedHeight: string = "80px";
const denyPopupReasonLabelKeyList: string[] = [
    "LabWorkRequiredLabel",
    "PatientMustMakeAnAppointmentLabel",
    "PatientNoLongerInThisPracticeLabel",
    "ModifiedPrescriptionExistsLabel",
    "OtherLabel"
];

export function PrescriptionRenewalsViewDenyPopup(props: IPrescriptionRenewalsViewDenyPopupProps): JSX.Element {
    const { getText } = useTranslation();

    const getReasonAutocompleteList = (): Immutable.List<ComboBoxDropDownItem> => {
        const denyReasonLabels = denyPopupReasonLabelKeyList.map((label: string, index: number) =>
            new ComboBoxDropDownItem(label, getText(label), index)
        );

        return Immutable.List(denyReasonLabels);
    };

    const [reason, setReason] = useState<string>(props.relatedMedication?.rejectReason ?? "");
    const [notes, setNotes] = useState<string>(props.relatedMedication?.rejectNotes ?? "");
    const [isClickableTextPanelVisible, setIsClickableTextPanelVisible] = useState<boolean>(false);

    const closeClickableTextPanel = (): void => {
        if (isClickableTextPanelVisible) setIsClickableTextPanelVisible(false);
    };

    const readonlyCssName = !!props.isReadonly ? styles.disabled : "";

    return (
        <div onClick={() => closeClickableTextPanel()}>
            <Popup
                title={getText("DenyRequestLabel")}
                width={350}
                cssClass={styles.popupContainer}
                onCloseDelegate={() => props.onCancel()}
                standardFooterProps={{
                    cancelButton: {
                        label: getText("CancelLabel"),
                        className: "btn btn-default push-right-5",
                        onClick: () => props.onCancel()
                    },
                    okButton: {
                        label: getText("SaveLabel"),
                        className: "btn btn-primary",
                        onClick: !!props.isReadonly ? null : () => props.onSave(reason, notes),
                        disabled: !!props.isReadonly
                    }
                }}
                centeredHorizontally
                manageOverlay
                draggable
                isOverflowVisible
                titleLowerCase
            >
                <div className={styles.container}>
                    <div className={styles.reason}>
                        <label className={styles.title}>{getText("ReasonLabel")}</label>
                        <ComboBoxControl
                            id="denyPopupReasonComboBoxControl"
                            onChange={!!props.isReadonly ? null : (code: string) => setReason(code)}
                            items={getReasonAutocompleteList()}
                            className={`${styles.reasonCombobox} ${readonlyCssName}`}
                            selectedCode={reason ?? ""}
                            hasEmptyItem
                            showTooltips
                            disabled={!!props.isReadonly}
                        />
                    </div>
                    <div className={styles.notes}>
                        <label className={styles.title}>{getText("NotesLabel")}</label>
                        <div className={styles.clickableTextPanel}>
                            <TextAreaAutoSize
                                id={notesTextareaId}
                                onTextChangeCallback={!!props.isReadonly ? null : (value: string) => setNotes(value)}
                                value={notes}
                                cssClass={readonlyCssName}
                                maxHeight={notesTextareaFixedHeight}
                                minHeight={notesTextareaFixedHeight}
                                disabled={!!props.isReadonly}
                            />

                            {!props.isReadonly && (
                                <>
                                    {isClickableTextPanelVisible && (
                                        <div className="clickable-text-container">
                                            <ClickableTextPanel
                                                cssClass="text-helper-container"
                                                context="PrescriptionRenewalNotes"
                                                inputElementId={notesTextareaId}
                                                onCloseEvent={() => closeClickableTextPanel()}
                                            />
                                        </div>
                                    )}
                                    <div className="text-right">
                                        <a onClick={() => setIsClickableTextPanelVisible(true)}>
                                            {getText("SelectFromListLabel")}
                                        </a>
                                    </div>
                                </>
                            )}
                        </div>
                    </div>
                </div>
            </Popup>
        </div>
    );
}
